import { api } from './api'
import { Campaign, CampaignType, TimingConfig, PaginatedResponse, ApiResponse } from '@/types'

export interface CampaignFilters {
  status?: Campaign['status']
  search?: string
  page?: number
  limit?: number
}

export interface CreateCampaignRequest {
  name: string
  mailboxId: number

  // Template-based system (new)
  campaignType?: CampaignType
  template1Id?: number
  template2Id?: number
  template3Id?: number
  template4Id?: number
  template5Id?: number
  timingConfig?: TimingConfig

  // Legacy inline content (backward compatibility)
  subject1?: string
  body1?: string
  subject2?: string
  body2?: string
  subject3?: string
  body3?: string
}

export interface UpdateCampaignRequest {
  name?: string
  status?: Campaign['status']
  mailboxId?: number
  campaignType?: CampaignType
  template1Id?: number | null
  template2Id?: number | null
  template3Id?: number | null
  template4Id?: number | null
  template5Id?: number | null
  timingConfig?: TimingConfig | null
  scheduledAt?: string
}

export const campaignService = {
  async fetchCampaigns(filters: CampaignFilters = {}): Promise<PaginatedResponse<Campaign>> {
    const response = await api.get<ApiResponse<Campaign[]>>('/campaigns', {
      params: { ...filters, _nocache: Date.now() },
    })
    // Backend returns { success: true, data: Campaign[], count: number }
    // Convert to PaginatedResponse format expected by frontend
    const campaigns = response.data.data || []
    const page = filters.page || 1
    const limit = filters.limit || 10
    return {
      data: campaigns,
      pagination: {
        page,
        limit,
        total: campaigns.length,
        totalPages: Math.ceil(campaigns.length / limit),
      },
    }
  },

  async getCampaign(id: number): Promise<Campaign> {
    const response = await api.get<ApiResponse<Campaign>>(`/campaigns/${id}`)
    return response.data.data as Campaign
  },

  async createCampaign(data: CreateCampaignRequest): Promise<Campaign> {
    const response = await api.post<ApiResponse<Campaign>>('/campaigns', data)
    return response.data.data as Campaign
  },

  async updateCampaign(id: number, data: UpdateCampaignRequest): Promise<Campaign> {
    const response = await api.put<ApiResponse<Campaign>>(`/campaigns/${id}`, data)
    return response.data.data as Campaign
  },

  async deleteCampaign(id: number): Promise<void> {
    await api.delete(`/campaigns/${id}`)
  },

  async startCampaign(id: number): Promise<Campaign> {
    const response = await api.post<ApiResponse<Campaign>>(`/campaigns/${id}/launch`)
    return response.data.data as Campaign
  },

  async pauseCampaign(id: number): Promise<Campaign> {
    const response = await api.post<ApiResponse<Campaign>>(`/campaigns/${id}/pause`)
    return response.data.data as Campaign
  },

  async resumeCampaign(id: number): Promise<Campaign> {
    const response = await api.post<ApiResponse<Campaign>>(`/campaigns/${id}/resume`)
    return response.data.data as Campaign
  },

  async stopCampaign(id: number): Promise<Campaign> {
    const response = await api.post<ApiResponse<Campaign>>(`/campaigns/${id}/stop`)
    return response.data.data as Campaign
  },

  async addClientsToCampaign(campaignId: number, clientIds: number[]): Promise<{ added: number; skipped: number; alreadyInCampaign: number[] }> {
    const response = await api.post<ApiResponse<{ added: number; skipped: number; alreadyInCampaign: number[] }>>(
      `/campaigns/${campaignId}/add-clients`,
      { clientIds }
    )
    return response.data.data as { added: number; skipped: number; alreadyInCampaign: number[] }
  },

  async getCampaignClients(
    campaignId: number,
    params: { page?: number; limit?: number } = {}
  ): Promise<{
    clients: Array<{
      id: number
      email: string
      fullName: string | null
      company: string | null
      status: string
      sequenceStatus: string | null
      currentStep: number | null
      nextSendAt: string | null
    }>
    total: number
    page: number
    limit: number
  }> {
    // Backend returns: { success: true, data: Client[], pagination: { page, limit, total, totalPages } }
    const response = await api.get<{
      success: boolean
      data: Array<{
        id: number
        email: string
        fullName: string | null
        company: string | null
        status: string
        sequenceStatus: string | null
        currentStep: number | null
        nextSendAt: string | null
      }>
      pagination: {
        page: number
        limit: number
        total: number
        totalPages: number
      }
    }>(`/campaigns/${campaignId}/clients`, { params })

    return {
      clients: response.data.data || [],
      total: response.data.pagination?.total || 0,
      page: response.data.pagination?.page || 1,
      limit: response.data.pagination?.limit || 20,
    }
  },

  async removeClientFromCampaign(campaignId: number, clientId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete<{ success: boolean; message: string }>(
      `/campaigns/${campaignId}/clients/${clientId}`
    )
    return response.data
  },
}
